/**
 * THE CRM transport. Every CRM screen and hook reaches the backend through the functions
 * below and through nothing else: this is the only module under `admin/crm/` that imports
 * `lib/api`, so a wire change is one edit here, and the Jest manual mock
 * (`./__mocks__/crmApi.js`) replaces the whole transport with contract-shaped fixtures.
 *
 * Each function returns the response BODY (`data`), a `Blob` for downloads, and throws
 * the axios error untouched on failure — the caller turns it into prose with
 * `extractApiError` and branches on `apiErrorCode` (lib/apiErrors.js).
 *
 * Wire naming is this module's business: the product-wide list reader (hooks/usePagedList)
 * builds `{page, pageSize}`, and the CRM contract (C0.01) reads `page` / `page_size`.
 */
// Relative on purpose: the "@" alias does not exist under Jest (see lib/LanguageContext.jsx).
import { api } from "../../../lib/api";
import { CRM_PATHS as P } from "../contract";

const body = (promise) => promise.then((r) => r.data);
const blob = (promise) => promise.then((r) => r.data);

/** `{page, pageSize, ...filters}` → the contract's query (`page_size`, arrays repeated). */
const listParams = ({ pageSize, ...rest } = {}) => {
  const out = { ...rest };
  if (pageSize !== undefined) out.page_size = pageSize;
  return out;
};

/** Repeated keys (`stage=lead&stage=active`) for array filters, as FastAPI reads them. */
const paramsSerializer = {
  serialize: (params) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params || {})) {
      if (v === undefined || v === null || v === "") continue;
      if (Array.isArray(v)) v.forEach((item) => q.append(k, String(item)));
      else q.append(k, String(v));
    }
    return q.toString();
  },
};

const get = (path, params) => body(api.get(path, { params: listParams(params), paramsSerializer }));

// ── Dashboard ──────────────────────────────────────────────────────────────────
export const getToday = (params) => get(P.today, params);
export const resendReminder = (bookingId) => body(api.post(P.resendReminder(bookingId), {}));
export const recordAttendance = (bookingId, outcome) => body(api.post(P.attendance(bookingId), { outcome }));
export const listNudges = (params) => get(P.nudges, params);
export const getNudgeDraft = (id) => get(P.nudgeDraft(id));
export const actOnNudge = (id, payload = {}) => body(api.post(P.nudgeAction(id), payload));
export const dismissNudge = (id) => body(api.post(P.nudgeDismiss(id), {}));
export const getPerformance = ({ period, from, to }) => get(P.performance, { period, from, to });
export const exportPerformance = ({ period, from, to }) =>
  blob(api.get(P.performanceExport, { params: { period, from, to }, paramsSerializer, responseType: "blob" }));
export const getSetup = () => get(P.setup);
export const enableReminders = () => body(api.post(P.setupReminders, {}));
export const dismissSetupBanner = () => body(api.post(P.setupBannerDismiss, {}));
export const stopPromotion = (id) => body(api.post(P.promotionStop(id), {}));

// ── Clients list ───────────────────────────────────────────────────────────────
export const listClients = (query) => get(P.clients, query);
export const createClient = (payload) => body(api.post(P.clients, payload));
export const bulkClients = (payload) => body(api.post(P.clientsBulk, payload));
/** Owner only: `{selection, expected_count}` → `{deleted}`; 409 DELETE_COUNT_MISMATCH when the selection resolves to another count. */
export const bulkDeleteClients = (payload) => body(api.post(P.clientsBulkDelete, payload));
export const exportClients = (payload) => blob(api.post(P.clientsExport, payload, { responseType: "blob" }));
export const listTags = () => get(P.tags);
export const listViews = () => get(P.views);
export const createView = (payload) => body(api.post(P.views, payload));
export const updateView = (id, payload) => body(api.put(P.view(id), payload));
export const deleteView = (id) => body(api.delete(P.view(id)));
export const getLatestImport = () => get(P.importsLatest);
/** Hides the Clients import banner for this job, for everyone (contract v1.5 CR-F-6). */
export const dismissImport = (id) => body(api.post(P.importDismiss(id), {}));

/**
 * The client whose email is EXACTLY `email` (case-insensitive, any of their emails):
 * `{client_id, href}` (contract C1.05 addendum). A miss rejects with 404
 * `CLIENT_NOT_FOUND` — callers branch on the code, since "no such client" is an answer,
 * not a failure.
 */
export const lookupClientByEmail = (email) => get(P.clientLookup, { email: String(email || "").trim() });

// ── Client detail ──────────────────────────────────────────────────────────────
export const getClient = (id) => get(P.client(id));
export const updateClient = (id, patch) => body(api.patch(P.client(id), patch));
export const setClientStage = (id, stage) => body(api.put(P.clientStage(id), { stage }));
export const resumeClientStage = (id) => body(api.post(P.clientStageResume(id), {}));
export const listTimeline = (id, query) => get(P.clientTimeline(id), query);
export const logInteraction = (id, payload) => body(api.post(P.clientInteractions(id), payload));
export const listNotes = (id, query) => get(P.clientNotes(id), query);
export const createNote = (id, payload) => body(api.post(P.clientNotes(id), payload));
export const updateNote = (id, noteId, payload) => body(api.put(P.clientNote(id, noteId), payload));
export const deleteNote = (id, noteId) => body(api.delete(P.clientNote(id, noteId)));
export const listDocuments = (id) => get(P.clientDocuments(id));
/** Uploads the file to the media library, then attaches the media row to the client. */
export const uploadDocument = async (id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  const media = await body(api.post(P.media, fd, { headers: { "Content-Type": "multipart/form-data" } }));
  return body(api.post(P.clientDocuments(id), { media_id: media.id, title: file.name }));
};
export const deleteDocument = (id, docId) => body(api.delete(P.clientDocument(id, docId)));
export const listLinks = (id) => get(P.clientLinks(id));
export const createLink = (id, payload) => body(api.post(P.clientLinks(id), payload));
export const deleteLink = (id, linkId) => body(api.delete(P.clientLink(id, linkId)));
export const listSessions = (id, query) => get(P.clientSessions(id), query);
export const listPurchases = (id, query) => get(P.clientPurchases(id), query);
export const listEmails = (id, query) => get(P.clientEmails(id), query);
export const getEmail = (id, sendId) => get(P.clientEmail(id, sendId));
export const sendClientEmail = (id, payload) => body(api.post(P.clientEmails(id), payload));
export const listMergeCandidates = (id, q) => get(P.clientMergeCandidates(id), { q });
export const getMergePreview = (id, otherId) => get(P.clientMergePreview(id), { other_id: otherId });
export const mergeClients = (id, otherId) => body(api.post(P.clientMerge(id), { other_id: otherId }));
export const unsubscribeClient = (id) => body(api.post(P.clientUnsubscribe(id), {}));
export const eraseClient = (id, confirmName) => body(api.post(P.clientErase(id), { confirm_name: confirmName }));
export const deleteClient = (id) => body(api.delete(P.client(id)));
export const exportClientData = (id) => blob(api.get(P.clientDataExport(id), { responseType: "blob" }));
export const getSessionNotes = (id) => get(P.clientSessionNotes(id));
export const saveSessionNote = (id, bookingId, text) => body(api.put(P.clientSessionNote(id, bookingId), { body: text }));
export const deleteSessionNote = (id, bookingId) => body(api.delete(P.clientSessionNote(id, bookingId)));
export const listClientPacks = (id) => get(P.clientPacks(id));
export const assignPack = (id, payload) => body(api.post(P.clientPacks(id), payload));
export const adjustPack = (id, balanceId, payload) => body(api.post(P.clientPackAdjust(id, balanceId), payload));

// ── Packs, sequences (Phase 2 routes, read here so P1 dialogs can list them) ─────
export const listPacks = (query) => get(P.packs, query);
export const listSequences = () => get(P.sequences);

// crm-module BEGIN C2.05 — Email Automation: sequences, broadcasts, birthday, pack
// renewal, sending domain (lane F1, append-only section).
export const getSequence = (id) => get(P.sequence(id));
export const createSequence = (payload) => body(api.post(P.sequences, payload));
export const updateSequence = (id, payload) => body(api.put(P.sequence(id), payload));
export const deleteSequence = (id) => body(api.delete(P.sequence(id)));
export const activateSequence = (id) => body(api.post(P.sequenceActivate(id), {}));
export const draftSequence = (id) => body(api.post(P.sequenceDraft(id), {}));
export const duplicateSequence = (id) => body(api.post(P.sequenceDuplicate(id), {}));
/**
 * A DRAFT email as a client receives it (sample client, practice locale, real buttons,
 * footer and sender): `{kind: "sequence_step"|"birthday"|"broadcast", subject, body_html,
 * sequence_id?, offset?, book_again_button?}` → `{subject, body_html, from_address,
 * reply_to, stream, locale}`.
 */
export const previewEmail = (draft) => body(api.post(P.emailPreview, draft));
/** That same draft email, to the signed-in practitioner's own address → `{sent, queued}`. */
export const sendEmailTest = (draft) => body(api.post(P.emailPreviewTest, draft));
export const previewBroadcast = (payload) => body(api.post(P.broadcastsPreview, payload));
export const listBroadcasts = () => get(P.broadcasts);
export const getBroadcast = (id) => get(P.broadcast(id));
export const sendBroadcast = (payload) => body(api.post(P.broadcasts, payload));
export const getBirthdaySettings = () => get(P.birthday);
export const updateBirthdaySettings = (payload) => body(api.put(P.birthday, payload));
export const getPackRenewalSettings = () => get(P.packRenewal);
export const updatePackRenewalSettings = (payload) => body(api.put(P.packRenewal, payload));
export const getSendingDomain = () => get(P.sendingDomain);
export const connectSendingDomain = (payload) => body(api.post(P.sendingDomain, payload));
export const verifySendingDomain = () => body(api.post(P.sendingDomainVerify, {}));
export const disconnectSendingDomain = () => body(api.delete(P.sendingDomain));
// crm-module END

// crm-module BEGIN C2.03 — Packs catalogue CRUD + void (lane F2, append-only section).
/** The Packs screen's "+ Create pack" (422 `SERVICE_PER_MINUTE`). */
export const createPack = (payload) => body(api.post(P.packs, payload));
export const updatePack = (id, payload) => body(api.put(P.pack(id), payload));
export const archivePack = (id) => body(api.post(P.packArchive(id), {}));
export const unarchivePack = (id) => body(api.post(P.packUnarchive(id), {}));
/** Corrects a wrongly-assigned balance (owner). Never touches Stripe/ledger/commission. */
export const voidPack = (clientId, balanceId, payload = {}) => body(api.post(P.clientPackVoid(clientId, balanceId), payload));
// crm-module END

// crm-module BEGIN C2.04 — Capture form admin (lane F2, append-only section).
export const getDefaultForm = () => get(P.formsDefault);
export const updateForm = (id, payload) => body(api.put(P.form(id), payload));
export const rotateFormKey = (id) => body(api.post(P.formRotateKey(id), {}));
// crm-module END

// ── Import wizard ──────────────────────────────────────────────────────────────
export const createImport = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return body(api.post(P.imports, fd, { headers: { "Content-Type": "multipart/form-data" } }));
};
export const getImport = (id) => get(P.importJob(id));
export const saveImportMapping = (id, mapping) => body(api.put(P.importMapping(id), { mapping }));
export const listImportDuplicates = (id, query) => get(P.importDuplicates(id), query);
export const saveImportDuplicates = (id, payload) => body(api.put(P.importDuplicates(id), payload));
export const saveImportConsent = (id, payload) => body(api.put(P.importConsent(id), payload));
export const getImportSummary = (id) => get(P.importSummary(id));
export const startImport = (id, stage) => body(api.post(P.importStart(id), { stage }));
export const deleteImport = (id) => body(api.delete(P.importJob(id)));
export const downloadSkippedRows = (id) => blob(api.get(P.importSkipped(id), { responseType: "blob" }));

// ── Booking (Book session) ─────────────────────────────────────────────────────
export const listBookingServices = () => get(P.bookingServices);
export const listOfferedStarts = (params) => get(P.offeredStarts, params);
export const createAdminBooking = (payload) => body(api.post(P.adminBookings, payload));

// ── Attachment pickers (Notes tab) ─────────────────────────────────────────────
export const listProducts = () => get(P.adminProducts);
export const listCourses = () => get(P.learnCourses);

// ── Pathways (the sequence editor's pathway trigger picker) ────────────────────
/** `PathwayListItem[]` (pathways-api-contract §5.1): every pathway of the practice. */
export const listPathways = () => get(P.pathways);

// crm-module BEGIN C3.02 — Team: invite/list/resend/remove (owner) + accept (any
// authenticated signed-in identity, from JoinPage) (lane F5, append-only section).
export const getCrmMe = () => get(P.me);
export const listTeam = () => get(P.team);
export const inviteTeamMember = (email) => body(api.post(P.teamInvites, { email }));
export const resendTeamInvite = (id) => body(api.post(P.teamInviteResend(id), {}));
export const removeTeamMember = (id) => body(api.delete(P.teamMember(id)));
export const acceptTeamInvite = (token) => body(api.post(P.teamAccept, { token }));

// crm-module BEGIN CR-F3-1 — CRM Settings: GET the whole `settings.crm` view, PUT only
// the fields that changed (the server writes named keys and nothing else).
export const getCrmSettings = () => get(P.settings);
export const updateCrmSettings = (fields) => body(api.put(P.settings, fields));
// crm-module END
// crm-module END

// crm-module BEGIN C3.02/C3.04 — Promote: quote + start a run (lane F4, append-only
// section). `stopPromotion` already lives in the Dashboard block above (P1 stub).
export const quotePromotion = (payload) => body(api.post(P.promotionQuote, payload));
export const createPromotion = (payload) => body(api.post(P.promotions, payload));
/** Every promotion this tenant has ever run — PromoteButton filters it to its own
 * `item_type`/`item_id` for the running/stop state; the Performance zone reads the
 * separate `active_promotions[]` subset embedded in `GET .../performance` instead. */
export const listPromotions = (query) => get(P.promotions, query);
// crm-module END
